import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Music, Search, Plus, ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { PlatformIcon } from '@/components/ui';
import { functions } from '@/services/firebase';
import { getPlatformName, detectPlatform } from '@/utils/platformDetector';
import type { MusicMatchResult, Platform } from '@/types';

// Generate a search URL for a platform given artist + title
const generateSearchUrl = (platform: Platform, artist: string, title: string): string => {
  const q = encodeURIComponent(`${artist} ${title}`);
  switch (platform) {
    case 'apple-music':
      return `https://music.apple.com/search?term=${q}`;
    case 'deezer':
      return `https://www.deezer.com/search/${encodeURIComponent(`${artist} ${title}`)}`;
    case 'youtube-music':
      return `https://music.youtube.com/search?q=${q}`;
    case 'amazon-music':
      return `https://music.amazon.com/search/${encodeURIComponent(`${artist} ${title}`)}`;
    case 'soundcloud':
      return `https://soundcloud.com/search/sounds?q=${q}`;
    case 'tidal':
      return `https://listen.tidal.com/search?q=${q}`;
    case 'pandora':
      return `https://www.pandora.com/search/${encodeURIComponent(`${artist} ${title}`)}`;
    case 'audiomack':
      return `https://audiomack.com/search?q=${q}`;
    default:
      return '';
  }
};

// Platforms to suggest via search when Odesli doesn't find them
const SEARCH_FALLBACK_PLATFORMS: Platform[] = [
  'apple-music',
  'deezer',
  'youtube-music',
  'amazon-music',
  'soundcloud',
  'tidal',
];

// Additional platforms for the manual section (less common)
const MANUAL_EXTRA_PLATFORMS: Platform[] = [
  'pandora',
  'audiomack',
];

interface MusicMatcherResponse {
  matches: MusicMatchResult[];
  title?: string;
  artistName?: string;
}

const musicMatcherFn = httpsCallable<{ url: string }, MusicMatcherResponse>(functions, 'musicMatcher');

async function fetchOdesliMatches(url: string): Promise<MusicMatcherResponse> {
  try {
    const result = await musicMatcherFn({ url });
    return result.data;
  } catch (err) {
    console.error('Music match error:', err);
    return { matches: [] };
  }
}

interface MusicMatchPanelProps {
  url: string;
  onMatchesChange: (matches: MusicMatchResult[]) => void;
}

const MusicMatchPanel = ({ url, onMatchesChange }: MusicMatchPanelProps) => {
  const { t } = useTranslation();

  // Auto-detected matches (from Odesli)
  const [matches, setMatches] = useState<MusicMatchResult[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Search-suggested matches (generated search URLs for missing platforms)
  const [searchSuggestions, setSearchSuggestions] = useState<MusicMatchResult[]>([]);
  const [searchSelected, setSearchSelected] = useState<Set<string>>(new Set());

  // Manual URL overrides (user can replace search URLs with direct links)
  const [manualUrls, setManualUrls] = useState<Record<string, string>>({});

  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showManual, setShowManual] = useState(false);

  const lastUrlRef = useRef('');
  const onMatchesChangeRef = useRef(onMatchesChange);
  onMatchesChangeRef.current = onMatchesChange;

  const sourcePlatform = detectPlatform(url);

  useEffect(() => {
    if (!url || url === lastUrlRef.current) return;

    const timer = setTimeout(async () => {
      setLoading(true);
      setSearched(false);
      setMatches([]);
      setSelected(new Set());
      setSearchSuggestions([]);
      setSearchSelected(new Set());
      setManualUrls({});
      setShowManual(false);
      lastUrlRef.current = url;

      const result = await fetchOdesliMatches(url);
      const { matches: foundMatches, title, artistName } = result;

      setMatches(foundMatches);
      setSearched(true);

      // Auto-select all Odesli matches
      const autoSelected = new Set(foundMatches.map((m) => m.platform));
      setSelected(autoSelected);

      // Generate search suggestions for missing platforms
      if (title && artistName) {
        const foundPlatforms = new Set(foundMatches.map((m) => m.platform));
        const suggestions: MusicMatchResult[] = [];

        for (const platform of SEARCH_FALLBACK_PLATFORMS) {
          if (platform === sourcePlatform || foundPlatforms.has(platform)) continue;
          const searchUrl = generateSearchUrl(platform, artistName, title);
          if (searchUrl) {
            suggestions.push({
              platform,
              url: searchUrl,
              title,
              artistName,
            });
          }
        }

        setSearchSuggestions(suggestions);
        // Auto-select search suggestions too
        const searchAutoSelected = new Set(suggestions.map((s) => s.platform));
        setSearchSelected(searchAutoSelected);

        // Sync everything to parent
        onMatchesChangeRef.current([...foundMatches, ...suggestions]);
      } else {
        onMatchesChangeRef.current(foundMatches);
      }

      setLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, [url, sourcePlatform]);

  // Compute extra manual platforms not covered by auto or search
  const coveredPlatforms = new Set([
    ...matches.map((m) => m.platform),
    ...searchSuggestions.map((s) => s.platform),
    sourcePlatform,
  ]);
  const manualExtraPlatforms = MANUAL_EXTRA_PLATFORMS.filter(
    (p) => !coveredPlatforms.has(p)
  );

  // Sync all results to parent
  const syncToParent = useCallback((
    currentMatches: MusicMatchResult[],
    currentSelected: Set<string>,
    currentSearchSuggestions: MusicMatchResult[],
    currentSearchSelected: Set<string>,
    currentManualUrls: Record<string, string>,
  ) => {
    const autoResults = currentMatches.filter((m) => currentSelected.has(m.platform));
    const searchResults = currentSearchSuggestions.filter((s) => currentSearchSelected.has(s.platform));

    // Manual URLs override search suggestion URLs
    const allResults = [...autoResults];
    for (const sr of searchResults) {
      const override = currentManualUrls[sr.platform]?.trim();
      if (override) {
        allResults.push({
          ...sr,
          url: override.startsWith('http') ? override : `https://${override}`,
        });
      } else {
        allResults.push(sr);
      }
    }

    // Pure manual entries (for extra platforms not in auto or search)
    for (const [platform, manualUrl] of Object.entries(currentManualUrls)) {
      const alreadyCovered = allResults.some((r) => r.platform === platform);
      if (!alreadyCovered && manualUrl.trim()) {
        allResults.push({
          platform: platform as Platform,
          url: manualUrl.trim().startsWith('http') ? manualUrl.trim() : `https://${manualUrl.trim()}`,
        });
      }
    }

    onMatchesChangeRef.current(allResults);
  }, []);

  const toggleAutoMatch = useCallback((platform: Platform) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);

      setMatches((cm) => {
        setSearchSuggestions((cs) => {
          setSearchSelected((css) => {
            setManualUrls((mu) => {
              syncToParent(cm, next, cs, css, mu);
              return mu;
            });
            return css;
          });
          return cs;
        });
        return cm;
      });
      return next;
    });
  }, [syncToParent]);

  const toggleSearchSuggestion = useCallback((platform: Platform) => {
    setSearchSelected((prev) => {
      const next = new Set(prev);
      if (next.has(platform)) next.delete(platform);
      else next.add(platform);

      setMatches((cm) => {
        setSelected((cs) => {
          setSearchSuggestions((ss) => {
            setManualUrls((mu) => {
              syncToParent(cm, cs, ss, next, mu);
              return mu;
            });
            return ss;
          });
          return cs;
        });
        return cm;
      });
      return next;
    });
  }, [syncToParent]);

  const handleManualUrlChange = useCallback((platform: string, value: string) => {
    setManualUrls((prev) => {
      const next = { ...prev, [platform]: value };
      setMatches((cm) => {
        setSelected((cs) => {
          setSearchSuggestions((ss) => {
            setSearchSelected((css) => {
              syncToParent(cm, cs, ss, css, next);
              return css;
            });
            return ss;
          });
          return cs;
        });
        return cm;
      });
      return next;
    });
  }, [syncToParent]);

  if (loading) {
    return (
      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
        <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">{t('linkPages.searchingPlatforms')}</span>
        </div>
      </div>
    );
  }

  if (!searched) return null;

  const hasAutoMatches = matches.length > 0;
  const hasSearchSuggestions = searchSuggestions.length > 0;

  return (
    <div className="space-y-3">
      {/* Auto-detected matches (direct links from Odesli) */}
      {hasAutoMatches && (
        <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl space-y-3">
          <div className="flex items-center gap-2">
            <Music className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span className="text-sm font-medium text-purple-700 dark:text-purple-300">
              {t('linkPages.musicDetected')}
            </span>
          </div>

          <p className="text-xs text-purple-600 dark:text-purple-400">
            {t('linkPages.musicDetectedDescription')}
          </p>

          <div className="space-y-2">
            {matches.map((match) => (
              <label
                key={match.platform}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selected.has(match.platform)}
                  onChange={() => toggleAutoMatch(match.platform)}
                  className="w-4 h-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                />
                <PlatformIcon platform={match.platform} size="sm" />
                <span className="text-sm text-dark-800 dark:text-white">
                  {getPlatformName(match.platform)}
                </span>
                {match.title && (
                  <span className="text-xs text-gray-500 truncate ml-auto">
                    {match.title}
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Search-suggested platforms (search URL fallback) */}
      {hasSearchSuggestions && (
        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl space-y-3">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              {t('linkPages.searchSuggestions')}
            </span>
          </div>

          <p className="text-xs text-blue-600 dark:text-blue-400">
            {t('linkPages.searchSuggestionsDescription')}
          </p>

          <div className="space-y-2">
            {searchSuggestions.map((suggestion) => (
              <div key={suggestion.platform} className="space-y-1">
                <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={searchSelected.has(suggestion.platform)}
                    onChange={() => toggleSearchSuggestion(suggestion.platform)}
                    className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <PlatformIcon platform={suggestion.platform} size="sm" />
                  <span className="text-sm text-dark-800 dark:text-white">
                    {getPlatformName(suggestion.platform)}
                  </span>
                  <span className="text-xs text-blue-500 ml-auto">{t('linkPages.searchLink')}</span>
                </label>
                {/* Optional: override with direct URL */}
                {searchSelected.has(suggestion.platform) && (
                  <input
                    type="text"
                    value={manualUrls[suggestion.platform] || ''}
                    onChange={(e) => handleManualUrlChange(suggestion.platform, e.target.value)}
                    placeholder={t('linkPages.replaceWithDirectLink')}
                    className="ml-9 w-[calc(100%-2.25rem)] text-xs px-3 py-1.5 bg-white dark:bg-dark-900 border border-blue-200 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-gray-500 placeholder:text-gray-400"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Extra manual platforms */}
      {manualExtraPlatforms.length > 0 && (
        <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-xl space-y-3">
          <button
            type="button"
            onClick={() => setShowManual((v) => !v)}
            className="flex items-center gap-2 w-full text-left"
          >
            <Plus className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300 flex-1">
              {t('linkPages.addManualPlatforms')}
            </span>
            {showManual
              ? <ChevronUp className="w-4 h-4 text-gray-400" />
              : <ChevronDown className="w-4 h-4 text-gray-400" />
            }
          </button>

          {showManual && (
            <div className="space-y-2 pt-1">
              {manualExtraPlatforms.map((platform) => (
                <div key={platform} className="flex items-center gap-2">
                  <PlatformIcon platform={platform} size="sm" />
                  <span className="text-xs font-medium text-gray-600 dark:text-gray-300 w-24 flex-shrink-0">
                    {getPlatformName(platform)}
                  </span>
                  <input
                    type="text"
                    value={manualUrls[platform] || ''}
                    onChange={(e) => handleManualUrlChange(platform, e.target.value)}
                    placeholder={`https://${platform}.com/...`}
                    className="flex-1 text-sm px-3 py-1.5 bg-white dark:bg-dark-900 border border-gray-200 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* No results at all */}
      {!hasAutoMatches && !hasSearchSuggestions && (
        <div className="p-4 bg-gray-50 dark:bg-dark-800 rounded-xl">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('linkPages.noMatchesFound')}
          </p>
        </div>
      )}
    </div>
  );
};

export default MusicMatchPanel;
